import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import type { FlightCartIntent, FlightCartLegMetadata, FlightCartReactivationMode } from 'tripPlanner/api/flightCart'
import {
  isCartableFlightSavedItemData,
  isChatFlightSavedItemData,
  isLeFlightSavedItemData,
  type CartableFlightSavedItemData,
  type LeFlightSavedItemData,
  type TransportConnection,
  type TransportOption,
} from 'tripPlanner/api/types'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import { isResolvedFlightJourneyId } from 'tripPlanner/components/Chat/Flights/flightJourneyId'
import {
  isBookedFlightOption,
  parseSearchParamsCurrency,
} from '../components/ShortlistedFlightsSection/flightShortlistUtils'
import { getCachedFareFamilySelection } from 'tripPlanner/components/Chat/Flights/fareFamilySelectionCache'
import type { FlightPartyPricingContext } from 'tripPlanner/components/Chat/Flights/buildFlightContext'

const AIRLINE_CODE_PATTERN = /^[A-Z0-9]{2}$/

/** The date portion of an ISO date-time (e.g. `2026-07-01T06:00` -> `2026-07-01`). */
export function getDateFromDateTime(dateTime: string | undefined): string {
  return dateTime?.split('T')[0] ?? ''
}

/**
 * Parse an `"Xh Ym"`-style flight duration into hours/minutes (each defaulting
 * to 0 when absent). Callers that need an optional duration object can treat a
 * `{ hours: 0, minutes: 0 }` result as "unknown".
 */
export function parseFlightDurationParts(duration: string | undefined): { hours: number; minutes: number } {
  const hours = Number(duration?.match(/(\d+)\s*h/)?.[1] ?? 0)
  const minutes = Number(duration?.match(/(\d+)\s*m/)?.[1] ?? 0)
  return { hours, minutes }
}

export type FlightCartPanelStatus =
  | 'active'
  | 'needs_flight_offer'
  | 'needs_flight_selection'
  | 'pending_hydration'

export interface ExtractedFlightCartPanelOption {
  productType: 'flight'
  tripItemId: number
  returnTripItemId: number | undefined
  pairedTripItemIds: Array<number>
  option: TransportOption
  returnOption: TransportOption | undefined
  savedItemData: CartableFlightSavedItemData
  returnSavedItemData: LeFlightSavedItemData | undefined
  cartStatus: FlightCartPanelStatus
  cartExcluded: boolean
  cartItemId: string | undefined
  cartItem: App.MultiCartFlightItem | undefined
  savedPrice: number | undefined
  savedRegularPrice: number | undefined
  savedCurrencyCode: string | undefined
  cartPrice: number | undefined
  cartRegularPrice: number | undefined
  cartCurrencyCode: string | undefined
  isLuxPlusFare: boolean | undefined
  includeInTotal: boolean
  isAvailabilityBlocked: boolean
}

export interface FlightCartIntentInput {
  savedItemData: CartableFlightSavedItemData
  hasActiveCartItem: boolean
  isRefresh: boolean
  selectedJourneyChanged: boolean
}

export interface FlightCartAvailabilityInput {
  cartExcluded: boolean
  isAvailabilityBlocked: boolean
}

export interface FlightLegMetadataInput {
  journey: App.JourneyV2
  savedItemData?: LeFlightSavedItemData
  searchId: string
  searchExpiry: number
  searchParams?: string
  partyPricing?: FlightPartyPricingContext
}

export interface FlightSavedPriceAndCurrency {
  price: number | undefined
  regularPrice: number | undefined
  currencyCode: string | undefined
  isLuxPlusFare: boolean | undefined
}

type FlightWithLuxPlusFareFields = App.Checkout.FlightItemFlight & {
  journeyFlight?: App.JourneyFlight
}

function getFlightCartItem(
  cartItemId: string | undefined,
  cartItemsById: Map<string, App.MultiCartItem>,
): App.MultiCartFlightItem | undefined {
  if (!cartItemId) return undefined
  const cartItem = cartItemsById.get(cartItemId)
  if (!cartItem || !isMultiCartFlightItem(cartItem)) return undefined
  if (!isFlightItem(cartItem.item)) return undefined
  return cartItem
}

export function getFlightCartItemId(option: TransportOption): string | undefined {
  return option.cartItemId ?? option.returnLeg?.cartItemId
}

export function getPairedFlightTripItemIds(option: TransportOption): Array<number> {
  return option.returnLeg ? [option.itemId, option.returnLeg.itemId] : [option.itemId]
}

function resolveFlightSavedPriceAndCurrency(
  savedItemData: CartableFlightSavedItemData,
): FlightSavedPriceAndCurrency {
  if (isChatFlightSavedItemData(savedItemData)) {
    return { price: undefined, regularPrice: undefined, currencyCode: undefined, isLuxPlusFare: undefined }
  }

  const price = savedItemData.fareTotalPrice
  const regularPrice = Number.isFinite(savedItemData.fareRegularTotalPrice) &&
    savedItemData.fareRegularTotalPrice! > (price ?? Infinity)
    ? savedItemData.fareRegularTotalPrice
    : undefined
  const currencyCode =
    savedItemData.currencyCode ??
    savedItemData.fareCurrencyCode ??
    parseSearchParamsCurrency(savedItemData.searchParams)

  const isLuxPlusFare = regularPrice !== undefined ? savedItemData.isLuxPlusFare ?? true : undefined

  return { price, regularPrice, currencyCode, isLuxPlusFare }
}

function resolveFlightCartPrice(cartItem: App.MultiCartFlightItem | undefined): number | undefined {
  if (!cartItem || !isFlightItem(cartItem.item)) return undefined
  return getFlightsTotal(cartItem.item)
}

function getSelectedFlightMemberPrice(flight: FlightWithLuxPlusFareFields): number {
  return flight.fareFamily?.cost?.all ?? flight.cost ?? flight.journeyFlight?.cost ?? 0
}

function getSelectedFlightRegularPrice(flight: FlightWithLuxPlusFareFields): number | undefined {
  const isSelectedLuxPlusFare = flight.fareFamily?.bookingInfo?.isLuxPlusFare === true || flight.journeyFlight?.isLuxPlusFare === true
  if (!isSelectedLuxPlusFare) return undefined
  return flight.fareFamily?.regularCost?.all ?? flight.journeyFlight?.regularPrice?.all.totalFare
}

function resolveFlightCartRegularPrice(cartItem: App.MultiCartFlightItem | undefined): number | undefined {
  if (!cartItem || !isFlightItem(cartItem.item)) return undefined

  const flights = cartItem.item.flights as Array<FlightWithLuxPlusFareFields>
  const hasLuxPlusRegularPrice = flights.some(flight => getSelectedFlightRegularPrice(flight) !== undefined)
  if (!hasLuxPlusRegularPrice) return undefined

  return flights.reduce((total, flight) => total + (getSelectedFlightRegularPrice(flight) ?? getSelectedFlightMemberPrice(flight)), 0)
}

export function isFlightSearchExpired(searchExpiry: number | undefined, now: number = Date.now()): boolean {
  return typeof searchExpiry === 'number' && searchExpiry <= now
}

export type SavedFlightSearchExpiryInput = Pick<
  ExtractedFlightCartPanelOption,
  'savedItemData' | 'returnSavedItemData'
>

/**
 * True when the provider search behind a flight's *saved* data has lapsed, so the
 * stored fare can no longer be turned into a cart item. A fare id only means
 * something inside the search that priced it, so an expired leg leaves nothing to
 * select and the traveller has to search again. Both legs count: a return pair is
 * priced by one search, and either half lapsing invalidates the selection.
 *
 * Distinct from `isActiveFlightAvailabilityBlocked`, which is about an item that
 * already converted into a cart item.
 */
export function isSavedFlightSearchExpired(
  item: SavedFlightSearchExpiryInput,
  now: number = Date.now(),
): boolean {
  const { savedItemData, returnSavedItemData } = item
  if (!isLeFlightSavedItemData(savedItemData)) return false
  if (isFlightSearchExpired(savedItemData.searchExpiry, now)) return true
  return !!returnSavedItemData && isFlightSearchExpired(returnSavedItemData.searchExpiry, now)
}

/**
 * Every saved provider-search expiry on a stop's flight options, both legs of a return
 * pair included. Feeds the cart expiry tick: a flight awaiting fare selection has no
 * cart item, so its lapse is invisible to the converted-item scan and nothing would
 * re-render the row as it happens.
 */
export function collectSavedFlightSearchExpiries(
  transports: Array<TransportConnection> | undefined,
): Array<number> {
  if (!transports) return []

  const expiries: Array<number> = []
  for (const transport of transports) {
    for (const option of transport.options) {
      for (const savedItemData of [option.savedItemData, option.returnLeg?.savedItemData]) {
        if (!isLeFlightSavedItemData(savedItemData)) continue
        if (savedItemData.searchExpiry !== undefined) expiries.push(savedItemData.searchExpiry)
      }
    }
  }

  return expiries
}

export type AutoConvertibleFlightLeg = Pick<
  ExtractedFlightCartPanelOption,
  'savedItemData' | 'returnSavedItemData' | 'returnOption'
>

/**
 * True when nothing is left to ask the traveller before this leg can become a
 * cart item, so the cart is free to convert it without any prompt:
 *  - a multi-city leg has no fare to choose at all (it is priced and booked as
 *    the whole itinerary), or
 *  - the fare was already picked when the flight was added to the trip, and the
 *    saved data still resolves to that same provider search — a fare id only
 *    means something inside the search that priced it, so a leg that would need
 *    a fresh search has nothing usable and must wait for "Select fares".
 */
export function canAutoConvertFlightLeg(item: AutoConvertibleFlightLeg): boolean {
  const { savedItemData, returnSavedItemData, returnOption } = item
  if (!isLeFlightSavedItemData(savedItemData) || !savedItemData.journeyKey) return false
  if (isFlightSearchExpired(savedItemData.searchExpiry)) return false
  if (savedItemData.flightGroupType === 'multi_city') return true

  if (!isResolvedFlightJourneyId(savedItemData.journeyId, savedItemData.journeyKey)) return false
  if (returnOption) {
    if (!returnSavedItemData?.journeyKey) return false
    if (!isResolvedFlightJourneyId(returnSavedItemData.journeyId, returnSavedItemData.journeyKey)) return false
    if (isFlightSearchExpired(returnSavedItemData.searchExpiry)) return false
  }

  return !!getCachedFareFamilySelection({
    departingJourneyKey: savedItemData.journeyKey,
    returningJourneyKey: returnSavedItemData?.journeyKey,
    searchId: savedItemData.searchId,
  })
}

export function isActiveFlightAvailabilityBlocked(
  cartItem: App.MultiCartFlightItem | undefined,
  now: number = Date.now(),
): boolean {
  if (!cartItem) return false
  if (cartItem.status === 'unavailable' || cartItem.status === 'dates_in_past') return true
  if (!isFlightItem(cartItem.item)) return false
  return isFlightSearchExpired(cartItem.item.expiry, now)
}

export function resolveFlightCartPanelStatus(
  option: TransportOption,
  cartItem: App.MultiCartFlightItem | undefined,
): FlightCartPanelStatus {
  const savedItemData = option.savedItemData
  if (isChatFlightSavedItemData(savedItemData)) return 'needs_flight_offer'
  if (!isLeFlightSavedItemData(savedItemData)) return 'pending_hydration'
  if (option.cartStatus === 'active' && cartItem) return 'active'
  if (option.cartStatus === 'active' && !cartItem) return 'needs_flight_selection'
  if (option.cartStatus === 'needs_flight_selection') return 'needs_flight_selection'
  if (!getFlightCartItemId(option)) return 'needs_flight_selection'
  return 'pending_hydration'
}

export function resolveFlightCartIntent({
  savedItemData,
  hasActiveCartItem,
  isRefresh,
  selectedJourneyChanged,
}: FlightCartIntentInput): FlightCartIntent {
  if (isChatFlightSavedItemData(savedItemData)) return 'convert_and_sync'
  if (isRefresh) return 'refresh'
  if (hasActiveCartItem || selectedJourneyChanged) return 'replace'
  return 'sync'
}

export function resolveFlightCartReactivationMode({
  cartExcluded,
  isAvailabilityBlocked,
}: FlightCartAvailabilityInput): FlightCartReactivationMode {
  return isAvailabilityBlocked && !cartExcluded ? 'availability_recovery' : 'preserve_exclusion'
}

export function buildFlightCartLegMetadata({
  journey,
  savedItemData,
  searchId,
  searchExpiry,
  searchParams,
  partyPricing,
}: FlightLegMetadataInput): FlightCartLegMetadata | undefined {
  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = journey.flightGroup.flights[journey.flightGroup.flights.length - 1]
  if (!firstFlight || !lastFlight) return undefined
  const airlineCode = firstFlight.marketingAirline.code.trim().toUpperCase()
  const isLuxPlusFare = journey.bookingInfo.isLuxPlusFare === true
  const regularPrice = isLuxPlusFare ? journey.regularPrice?.all.totalFare : undefined
  const regularPricePerPerson = isLuxPlusFare ? journey.regularPrice?.adult?.totalFare : undefined

  return {
    source: 'le_flight',
    flightGroupId: savedItemData?.flightGroupId ?? journey.flightGroup.id,
    legOrder: savedItemData?.legOrder,
    departureAirport: firstFlight.departureAirport,
    arrivalAirport: lastFlight.arrivalAirport,
    departureDate: firstFlight.departureDateTime.slice(0, 10),
    arrivalDate: lastFlight.arrivalDateTime.slice(0, 10),
    departureTime: firstFlight.departureDateTime,
    arrivalTime: lastFlight.arrivalDateTime,
    airline: journey.carrierName,
    ...(AIRLINE_CODE_PATTERN.test(airlineCode) ? { airlineCode } : {}),
    flightNumber: firstFlight.flightNumber,
    carrierLogo: journey.carrierLogo,
    ...(partyPricing?.fareTotalPrice !== undefined && { fareTotalPrice: partyPricing.fareTotalPrice }),
    ...(partyPricing?.fareRegularTotalPrice !== undefined && { fareRegularTotalPrice: partyPricing.fareRegularTotalPrice }),
    ...(partyPricing?.fareCurrencyCode && { fareCurrencyCode: partyPricing.fareCurrencyCode }),
    price: journey.price.adult.totalFare,
    ...(regularPrice !== undefined && { regularPrice }),
    ...(regularPricePerPerson !== undefined && { regularPricePerPerson }),
    ...(isLuxPlusFare && { isLuxPlusFare: true }),
    currencyCode: journey.currencyCode,
    searchId,
    journeyKey: journey.bookingInfo.journeyKey,
    journeyId: journey.id,
    searchExpiry,
    searchParams: searchParams ?? savedItemData?.searchParams,
  }
}

export function extractFlightCartPanelOptions(
  transports: Array<TransportConnection> | undefined,
  cartItemsById: Map<string, App.MultiCartItem>,
  now: number = Date.now(),
): Array<ExtractedFlightCartPanelOption> {
  if (!transports) return []

  const results: Array<ExtractedFlightCartPanelOption> = []
  const seenItemIds = new Set<number>()

  for (const transport of transports) {
    for (const option of transport.options) {
      if (seenItemIds.has(option.itemId)) continue
      if (isBookedFlightOption(option)) continue
      const savedItemData = option.savedItemData
      if (isChatFlightSavedItemData(savedItemData)) continue
      if (!isCartableFlightSavedItemData(savedItemData)) continue

      const returnOption = option.returnLeg
      const returnSavedItemData = returnOption?.savedItemData
      const cartItemId = getFlightCartItemId(option)
      const cartItem = getFlightCartItem(cartItemId, cartItemsById)
      const cartStatus = resolveFlightCartPanelStatus(option, cartItem)
      const pairedTripItemIds = getPairedFlightTripItemIds(option)
      const savedPrice = resolveFlightSavedPriceAndCurrency(savedItemData)
      const cartPrice = resolveFlightCartPrice(cartItem)
      const cartRegularPrice = resolveFlightCartRegularPrice(cartItem)
      const isAvailabilityBlocked = isActiveFlightAvailabilityBlocked(cartItem, now)

      for (const itemId of pairedTripItemIds) {
        seenItemIds.add(itemId)
      }

      results.push({
        productType: 'flight',
        tripItemId: option.itemId,
        returnTripItemId: returnOption?.itemId,
        pairedTripItemIds,
        option,
        returnOption,
        savedItemData,
        returnSavedItemData: isLeFlightSavedItemData(returnSavedItemData) ? returnSavedItemData : undefined,
        cartStatus,
        cartExcluded: option.cartExcluded ?? returnOption?.cartExcluded ?? false,
        cartItemId,
        cartItem,
        savedPrice: savedPrice.price,
        savedRegularPrice: savedPrice.regularPrice,
        savedCurrencyCode: savedPrice.currencyCode,
        cartPrice,
        cartRegularPrice,
        cartCurrencyCode: savedPrice.currencyCode,
        isLuxPlusFare: cartRegularPrice !== undefined || savedPrice.isLuxPlusFare,
        includeInTotal: cartStatus === 'active' && !isAvailabilityBlocked,
        isAvailabilityBlocked,
      })
    }
  }

  return results
}

export function toFlightCartPanelProjectionSignature(
  item: ExtractedFlightCartPanelOption,
): string | undefined {
  if (!item.cartItemId) return undefined
  return JSON.stringify({
    productType: item.productType,
    tripItemId: item.tripItemId,
    returnTripItemId: item.returnTripItemId,
    pairedTripItemIds: item.pairedTripItemIds,
    cartItemId: item.cartItemId,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
  })
}

export function hasMissingLinkedFlightCartPanelItem(
  flightItems: Array<ExtractedFlightCartPanelOption>,
): boolean {
  return flightItems.some(item => !!item.cartItemId && !item.cartItem)
}

export function hasLinkedFlightCartPanelItem(
  flightItems: Array<ExtractedFlightCartPanelOption>,
): boolean {
  return flightItems.some(item => !!item.cartItemId)
}
